/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { CertModel } from '../models';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { CertificatesEnum } from '../enums/api/certs.enums';

@Injectable()
export class CertsApiService {

    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * API call to get the all certificates
     * @param options
     * @returns {any}
     */
    public getCerts(options?: any): Observable<CertModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CERTS,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to get the all the CSRs
     * @param options
     * @returns {any}
     */
    public getCSRs(options?: any): Observable<CertModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CSRS,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get CAs list.
     * @param options
     */
    public getCAs(options?: any): Observable<CertModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CAS,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get CRLs list.
     * @param options
     */
    public getCRLs(options?: any): Observable<CertModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CRLS,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to get a specific certificate
     * @param id
     * @param options
     * @returns {any}
     */
    public getCert(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CERTS + '/' + id,
                moduleAuth, false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to add a new certificate
     * @param cert
     * @param options
     * @returns {any}
     */
    public addCert(cert: CertModel, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(
                CertificatesEnum.POST_CERT,
                cert,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to update a specific certificate
     * @param cert
     * @param options
     * @returns {any}
     */
    public updateCert(cert: CertModel, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.GET_CERTS + '/' + cert.id,
                cert,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to update a specific certificate
     * @param cert
     * @param options
     * @returns {any}
     */
    public updateCSR(cert: CertModel, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.PUT_CSR(cert.id),
                cert,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Apply a CSR.
     * @param cert
     * @param options
     */
    public applyCSR(cert: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.PUT_APPLY_CSR(cert.id),
                cert,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to get a specific CSR
     * @param id
     * @param options
     * @returns {any}
     */
    public getCSR(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CSR(id),
                moduleAuth, false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to create a new CSR
     * @param csr
     * @param options
     * @returns {any}
     */
    public createCSR(csr: CertModel, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(
                CertificatesEnum.POST_CSR,
                csr,
                moduleAuth,
                false
            ).subscribe({
                next: (data: CertModel): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to renew a self signed cert
     * @param id
     * @param options
     * @returns {any}
     * // TODO: This is missing data.. BROKEN?
     */
    public renewSelfSigned(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.PUT_SELF_CERT_RENEW(id),
                null,
                moduleAuth,
                false
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to delete a cert
     * @param id
     * @param options
     * @returns {any}
     */
    public deleteCert(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(
                CertificatesEnum.DELETE_CERT(id),
                moduleAuth,
                false
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to delete a cert
     * @param id
     * @param options
     * @returns {any}
     */
    public deleteCSR(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(
                CertificatesEnum.DELETE_CSR(id),
                moduleAuth,
                false
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get signature algorithms.
     * @param options
     */
    public getSignatureAlgorithms(options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_SIG_ALGORITHMS,
                moduleAuth, false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get a CA.
     * @param id
     * @param options
     */
    public getCA(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CA(id),
                moduleAuth, false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Create a CA.
     * @param ca
     * @param options
     */
    public createCA(ca: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(
                CertificatesEnum.POST_CA,
                ca,
                moduleAuth,
                false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Update a CA.
     * @param caId
     * @param ca
     * @param options
     */
    public updateCA(caId: any, ca: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.PUT_CA(caId),
                ca,
                moduleAuth,
                false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to delete a CA
     * @param id
     * @param options
     * @returns {any}
     */
    public deleteCA(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(
                CertificatesEnum.DELETE_CA(id),
                moduleAuth,
                false
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Create a CRL.
     * @param crl
     * @param options
     */
    public createCRL(crl: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(
                CertificatesEnum.POST_CRL,
                crl,
                moduleAuth,
                false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get a crl.
     * @param id
     * @param options
     */
    public getCRL(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                CertificatesEnum.GET_CRL(id),
                moduleAuth, false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Update a CRL
     * @param crl
     * @param options
     */
    public updateCRL(crl: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                CertificatesEnum.PUT_CRL(crl.id),
                crl,
                moduleAuth,
                false
            ).subscribe({
                next: (data: string[]): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * API call to delete a CRL
     * @param id
     * @param options
     * @returns {any}
     */
    public deleteCRL(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(
                CertificatesEnum.DELETE_CRL(id),
                moduleAuth,
                false
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
